import React, { useState, useEffect, useMemo } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  FileCheck,
  Users,
  BarChart3,
  LogOut,
  Menu,
  X,
  Bell,
  ChevronDown,
  Shield,
  UserCheck,
  Brain
} from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { useBrokerData } from '../../context/BrokerDataContext';
import { getBrokerConfig, getBrokerFromPath } from '../../config/brokerConfig';
import './AdminLayout.css';

function AdminLayout() {
  const { isAdminAuthenticated, adminUser, logoutAdmin } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);

  // Get broker config based on URL path
  const brokerId = getBrokerFromPath(location.pathname);
  const brokerConfig = useMemo(() => getBrokerConfig(brokerId), [brokerId]);

  // Use broker-specific data from BrokerDataContext
  const {
    transactionsNeedingAttention: brokerTransactionsNeedingAttention,
    pendingRegistrationsCount
  } = useBrokerData();

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!isAdminAuthenticated) {
      navigate(`/${brokerId}/admin/login`);
    }
  }, [isAdminAuthenticated, navigate, brokerId]);

  const handleLogout = () => {
    logoutAdmin();
    navigate(`/${brokerId}/admin/login`);
  };

  // Use broker-specific counts
  const transactionsNeedingAttentionCount = brokerTransactionsNeedingAttention?.length || 0;
  const pendingAccountsCount = pendingRegistrationsCount || 0;

  // Dynamically generate nav items based on current broker
  const navItems = [
    { path: `/${brokerId}/admin/dashboard`, icon: LayoutDashboard, label: 'Dashboard' },
    { path: `/${brokerId}/admin/transactions`, icon: FileCheck, label: 'Transactions', badge: transactionsNeedingAttentionCount },
    { path: `/${brokerId}/admin/account-verification`, icon: UserCheck, label: 'Account Verification', badge: pendingAccountsCount },
    { path: `/${brokerId}/admin/users`, icon: Users, label: 'Users' },
    { path: `/${brokerId}/admin/reports`, icon: BarChart3, label: 'Reports' },
    { path: `/${brokerId}/admin/ai-settings`, icon: Brain, label: 'AI Settings' },
  ];

  if (!isAdminAuthenticated) {
    return null;
  }

  return (
    <div className="admin-layout">
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside
        className={`admin-sidebar ${sidebarOpen ? 'open' : ''}`}
        style={{ background: `linear-gradient(180deg, ${brokerConfig.primaryColor} 0%, ${brokerConfig.secondaryColor} 100%)` }}
      >
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <img src={brokerConfig.logo} alt={brokerConfig.name} className="sidebar-savexa-logo" />
          </div>
          <button className="sidebar-close" onClick={() => setSidebarOpen(false)}>
            <X size={24} />
          </button>
        </div>

        <nav className="sidebar-nav">
          {navItems.map(item => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
              onClick={() => setSidebarOpen(false)}
            >
              <item.icon size={20} />
              <span>{item.label}</span>
              {item.badge > 0 && <span className="nav-badge">{item.badge}</span>}
            </NavLink>
          ))}
        </nav>

        <div className="sidebar-footer">
          <button className="logout-btn" onClick={handleLogout}>
            <LogOut size={20} />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="admin-content">
        {/* Top Header */}
        <header className="admin-header">
          <div className="header-left">
            <button className="menu-toggle" onClick={() => setSidebarOpen(true)}>
              <Menu size={24} />
            </button>
            <h1 className="page-title">
              {navItems.find(item => location.pathname.startsWith(item.path))?.label || 'Dashboard'}
            </h1>
          </div>

          <div className="header-right">
            <button className="notification-btn">
              <Bell size={20} />
              {transactionsNeedingAttentionCount > 0 && <span className="notification-badge">{transactionsNeedingAttentionCount}</span>}
            </button>

            <div className="profile-menu-wrapper">
              <button
                className="profile-btn"
                onClick={() => setShowProfileMenu(!showProfileMenu)}
              >
                <div className="profile-avatar">
                  {adminUser?.name?.charAt(0) || 'A'}
                </div>
                <span className="profile-name">{adminUser?.name || 'Admin'}</span>
                <ChevronDown size={16} />
              </button>

              {showProfileMenu && (
                <div className="profile-dropdown">
                  <div className="profile-info">
                    <div className="profile-avatar large">
                      {adminUser?.name?.charAt(0) || 'A'}
                    </div>
                    <div className="profile-details">
                      <span className="profile-name">{adminUser?.name || 'Admin'}</span>
                      <span className="profile-email">{adminUser?.email || 'admin@anbvision.com'}</span>
                    </div>
                  </div>
                  <div className="profile-actions">
                    <button onClick={handleLogout}>
                      <LogOut size={16} />
                      <span>Logout</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="admin-main">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

export default AdminLayout;
